import React, { useState, useEffect, useCallback } from 'react'
import {
  Stack,
  Button,
  SimpleModal,
  FormControl,
  Input,
  Textarea,
  DateChooser,
  UserChooser,
  Alert,
  Text
} from '@/components/lib'
import api from '@/api'
import utils from '@/utils/utils'
import useStore from '@/store'

/**
 * Create or edit a participant calendar event (staff-only).
 */
export default function ParticipantEventEditor ({
  isOpen,
  onClose,
  trialId,
  participantId,
  participant,
  /** Existing event to edit; omit or null for create. */
  event,
  /** When creating, optional default start (e.g. calendar day + time). */
  defaultStartsAt,
  onSaved
}) {
  const { getTrialUsers } = useStore()
  const trialUsers = getTrialUsers(trialId)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [startsAt, setStartsAt] = useState(null)
  const [personnelIds, setPersonnelIds] = useState([])
  const [saving, setSaving] = useState(false)

  const isEdit = !!(event && event._id)

  useEffect(() => {
    if (!isOpen) return
    if (isEdit) {
      setTitle(event.title || '')
      setDescription(event.description || '')
      setStartsAt(event.startsAt ? new Date(event.startsAt) : null)
      setPersonnelIds(
        Array.isArray(event.personnel)
          ? event.personnel.map((id) =>
            typeof id === 'object' && id?._id ? id._id : id
          )
          : []
      )
    } else {
      setTitle('')
      setDescription('')
      setStartsAt(defaultStartsAt ?? null)
      setPersonnelIds([])
    }
  }, [isOpen, isEdit, event, defaultStartsAt])

  const handleClose = useCallback(() => {
    if (!saving) onClose?.()
  }, [saving, onClose])

  function onPersonnelChosen (value) {
    setPersonnelIds(
      Array.isArray(value)
        ? value.map((v) => (typeof v === 'object' && v?._id ? v._id : v))
        : []
    )
  }

  function handleSave () {
    const t = (title || '').trim() || 'Event'
    if (!startsAt) {
      utils.toast('error', 'Please set a date and time')
      return
    }
    const payload = {
      title: t,
      description: (description || '').trim(),
      startsAt: startsAt instanceof Date ? startsAt.toISOString() : startsAt,
      personnel: personnelIds
    }
    if (isEdit) {
      setSaving(true)
      api.participantEvents.update(
        trialId,
        participantId,
        event._id,
        payload,
        (ev) => {
          setSaving(false)
          utils.toast('success', 'Event updated')
          onSaved?.(ev)
          onClose?.()
        },
        (err) => {
          setSaving(false)
          utils.toast('error', err.message)
        }
      )
      return
    }
    setSaving(true)
    api.participantEvents.create(
      trialId,
      participantId,
      payload,
      (ev) => {
        setSaving(false)
        utils.toast('success', 'Event created')
        onSaved?.(ev)
        onClose?.()
      },
      (err) => {
        setSaving(false)
        utils.toast('error', err.message)
      }
    )
  }

  return (
    <SimpleModal
      size='sm'
      isOpen={isOpen}
      onClose={handleClose}
      header={isEdit ? 'Edit event' : 'New event'}
      body={
        <Stack gap={4}>
          <FormControl label='Title' required>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={participant?.id ? `Event for ${participant.id}` : 'Title'}
            />
          </FormControl>
          <FormControl label='Description'>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder='Optional details'
              rows={4}
            />
          </FormControl>
          <FormControl label='Date and time' required>
            <Stack direction='row' gap={2} alignItems='center'>
              <DateChooser
                withTime
                value={startsAt}
                onChange={(date) => setStartsAt(date)}
                placeholder='Select date and time'
              />
              <Text fontSize='sm' color='fg.subtle'>{Intl.DateTimeFormat().resolvedOptions().timeZone}</Text>
            </Stack>
          </FormControl>
          <FormControl label='Personnel' helperText='Trial users associated with this event.'>
            <UserChooser
              multiple
              placeholder='Select personnel'
              users={trialUsers}
              selectedUsers={personnelIds}
              onChosen={onPersonnelChosen}
            />
          </FormControl>
          {!isEdit && (
            <Alert>
              Trialflare will send the participant and any selected personnel a message containing the details of the event.
            </Alert>
          )}
        </Stack>
      }
      footer={
        <Stack direction='row' gap={2} justifyContent='flex-end'>
          <Button variant='ghost' onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button colorPalette='primary' onClick={handleSave} loading={saving}>
            {isEdit ? 'Save' : 'Create event'}
          </Button>
        </Stack>
      }
    />
  )
}
